import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import { theme } from "../theme"; // Assuming you are using a theme
import { lighten, rgba } from "polished";
import {  Flex } from "./layout";
import { FabButton } from "./fab-button";
import { TbCircleX } from "react-icons/tb";

const SnackbarStyle = styled(Flex)(({ color, Colorvariant = "light", variant = "text", sx = {}, position = "md" }) => {
  const colorObj = theme.colors[color] || theme.colors.main;
  const bgColorValue = colorObj[Colorvariant] || colorObj.main;
  const colorLight = lighten(0.25 , bgColorValue);

  let backgroundColor, textColor, border;

  // Background and border for different variants
  switch (variant) {
    case "outlined":
      backgroundColor = theme.colors.light.light;
      border = `0.0625rem solid ${rgba(bgColorValue, 1)}`;
      break;
    case "filled":
      backgroundColor = colorLight;
      border = "transparent";
      break;
    case "contained":
      backgroundColor = bgColorValue;
      border = "transparent";
      break;
    default:
      backgroundColor = "transparent";
      border = "transparent";
  }
 switch (color) {
    case "primary":
      textColor = ["outlined", "filled"].includes(variant)
        ? theme.colors.primary.main
        : theme.colors.light.light;
      break;
    case "secondary":
      textColor = ["outlined", "filled"].includes(variant)
        ? theme.colors.secondary.main
        : theme.colors.light.light;
      break;
    case "success":
      textColor = ["outlined", "filled"].includes(variant)
        ? theme.colors.success.main
        : theme.colors.light.light;
      break;
    case "danger":
      textColor = ["outlined", "filled"].includes(variant)
        ? theme.colors.danger.main
        : theme.colors.light.light;
      break;
    case "dark":
      textColor = ["outlined", "filled"].includes(variant)
        ? theme.colors.dark.main
        : theme.colors.light.light;
      break;  
    case "light":
      textColor = ["outlined", "filled"].includes(variant)
        ? theme.colors.light.main
        : theme.colors.primary.light;
      break;  
    default:
      textColor = theme.colors.default.dark;
  }

  // Text color based on color and variant
  // textColor = variant === "outlined" || variant === "filled" || variant === "text"
  //   ? theme.colors[color]?.main || theme.colors.default.dark
  //   : theme.colors.light.light;

  // Positioning styles
  const positions = {
    "top-left": { top: "1rem", left: "1rem" },
    "top-center": { top: "1rem", left: "50%", transform: "translateX(-50%)" },
    "top-right": { top: "1rem", right: "1rem" },
    "bottom-left": { bottom: "1rem", left: "1rem" },
    "bottom-center": { bottom: "1rem", left: "50%", transform: "translateX(-50%)" },
    "bottom-right": { bottom: "1rem", right: "1rem" }
  };

  return {
    display: "flex",
    alignItems: "center",
    width: "auto",
    textAlign: "center",
    justifyContent: "center",
    backgroundColor,
    color: textColor,
    fontStyle: "normal",
    fontSize: "0.875rem",
    padding: "0.5rem 1.25rem",
    borderRadius: theme.radius.element,
    fontWeight: 500,
    outline: "none",
    border,
    overflow: "hidden",
    position: "fixed",
    fontFamily: theme.typography.fontFamily,
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
    zIndex:999,
    ...positions[position],
    "& .iconBox": {
      width: "1.5rem",
      height: "1.5rem",
      alignItems: "center",
      justifyContent: "center",
      marginInlineEnd: "0.5rem",
      position: "relative",
    },
    ...sx,
  };
});

export const Snackbar = ({
  message,
  color = "primary",
  variant = "contained",
  position = "top-right",
  onClose,
  onOpen = false,
  icon,
  duration = 3000,
  clearable = false,
}) => {
  const [isExiting, setIsExiting] = useState(false);
  const getFabButtonColor = (variant) => {
    if (variant === "contained") return "light"; // Specific rule for 'contained'
    return color; // Fallback for other variants
  };
  useEffect(() => {
    if (onOpen) {
      const timer = setTimeout(() => {
        setIsExiting(true);
        setTimeout(() => {
          if (onClose) onClose();
        }, duration); // Match with exit animation duration
      }, duration);

      return () => clearTimeout(timer);
    }
  }, [duration, onClose, onOpen]);

  if (!onOpen) return null;

  return (
    <SnackbarStyle
      color={color}
      variant={variant}
      position={position}
      isExiting={isExiting}
    >
      {icon && <Flex className="iconBox">{icon}</Flex>}
      <div>{message}</div>
      {clearable && 
        <FabButton
          color={getFabButtonColor(variant)} 
          size="xs"
          sx={{marginInlineEnd:"-0.625rem",marginInlineStart:"0.25rem",}}
          onClick={() => {
            setIsExiting(true);
            setTimeout(() => {
              if (onClose) onClose();
            }, 3000); // Match animation duration
          }}
          icon={<TbCircleX/>}
       />
      }
    </SnackbarStyle>
  );
};
Snackbar.defaultProps = {
  message: "This is a notification.",
  color: "primary",
  variant: "contained",
  position: "top-right",
  duration: 3000, // 3 seconds
  onOpen: false,  // Snackbar is closed by default
  clearable: false, // No close button by default
  icon: null, // No icon by default
};


Snackbar.propTypes = {
  message: PropTypes.string.isRequired,
  color: PropTypes.oneOf([
    "default",
    "primary",
    "secondary",
    "success",
    "danger",
    "warning",
    "info",
    "light",
    "dark",
  ]),
  variant: PropTypes.oneOf(["text", "outlined", "filled", "contained"]),
  position: PropTypes.oneOf([
    "top-left",
    "top-center",
    "top-right",
    "bottom-left",
    "bottom-center",
    "bottom-right",
  ]),
  onClose: PropTypes.func,
  onOpen: PropTypes.bool,
  icon: PropTypes.node,
  duration: PropTypes.number,
  clearable: PropTypes.bool,
};
